import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { type ApiError, api, qs, unwrap } from '../api/client';
import { useAccounts } from '../api/hooks';
import type { Account, Items, Media, OnboardingView } from '../api/types';
import { useSession } from '../auth/session';
import {
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorAlert,
  Field,
  fmtDate,
  Modal,
  PageHeader,
  relTime,
  useToast,
} from '../components/ui';

/** The PRD's permission checklist, keyed by the Meta scope that grants it. */
const PERMISSION_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['instagram_business_basic', 'Basic (profile and media)'],
  ['instagram_business_manage_messages', 'Messaging'],
  ['instagram_business_manage_comments', 'Comments'],
  ['instagram_business_content_publish', 'Content publishing'],
];

const plural = (k: 'post' | 'reel' | 'story'): string => (k === 'story' ? 'stories' : `${k}s`);

const PLATFORMS = [
  'Instagram',
  'Facebook',
  'YouTube',
  'TikTok',
  'LinkedIn',
  'Threads',
  'Telegram',
  'Snapchat',
  'WhatsApp',
] as const;

/** The gateway id is a config value; this is how it is written in prose. */
const GATEWAY_LABELS: Record<string, string> = {
  postroutine: 'PostRoutine',
  instagram: 'Instagram',
};
const gatewayLabel = (g: string) => GATEWAY_LABELS[g] ?? g;

/**
 * SOC-35: the three steps between a fresh workspace and a working comment automation, and which
 * one the operator is on. Two of the three AutoDM cannot do itself: only the account's owner can
 * accept PostRoutine's tester invitation, and only PostRoutine can connect the account. So the
 * card names them in those words instead of showing an empty table that reads as "you have none".
 */
const STAGE: Record<
  OnboardingView['stage'],
  { tone: 'info' | 'success' | 'error'; title: string; body: string }
> = {
  no_key: {
    tone: 'info',
    title: 'Step 1 of 3 — add your API key.',
    body: 'AutoDM reaches Instagram through your own PostRoutine workspace, so it needs that workspace’s API key before it can see anything.',
  },
  no_accounts: {
    tone: 'info',
    title: 'Step 2 of 3 — the account is not a tester yet.',
    body: 'The key works, but PostRoutine holds no Instagram account for it. Almost always this means the account has not been added as a tester of PostRoutine’s Meta app, or the invitation has not been accepted.',
  },
  link: {
    tone: 'info',
    title: 'Step 3 of 3 — link the account to AutoDM.',
    body: 'PostRoutine holds an account that AutoDM has not linked yet. Linking is instant: there is no second Instagram login.',
  },
  fix_in_postroutine: {
    tone: 'error',
    title: 'An account needs attention in PostRoutine.',
    body: 'Its token has expired or a permission is missing. Reconnect it in PostRoutine, then re-check here.',
  },
  ready: {
    tone: 'success',
    title: 'Connected.',
    body: 'Comments on these accounts are polled and will trigger your published automations.',
  },
};

/** done, the operator's move, or the gateway's. */
function Step({
  n,
  title,
  state,
  children,
}: {
  n: number;
  title: string;
  state: 'done' | 'todo' | 'blocked';
  children?: ReactNode;
}) {
  const tone =
    state === 'done' ? 'badge-success' : state === 'blocked' ? 'badge-danger' : 'badge-warning';
  const label = state === 'done' ? 'done' : state === 'blocked' ? 'needs attention' : 'to do';
  return (
    <li className="conn-step" data-testid={`conn-step-${n}`} data-state={state}>
      <div className="row" style={{ gap: 8 }}>
        <span className="conn-step-n">{n}</span>
        <strong>{title}</strong>
        <span className={`badge ${tone}`}>{label}</span>
      </div>
      {children ? <div className="conn-step-body">{children}</div> : null}
    </li>
  );
}

/** SOC-33: the workspace's own key, written once and afterwards known only by its last four. */
function KeyStep({ view, gateway }: { view?: OnboardingView; gateway: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { me, refresh, can } = useSession();
  const [draft, setDraft] = useState('');
  const [replacing, setReplacing] = useState(false);
  const label = gatewayLabel(gateway);
  const last4 = me?.workspace?.postroutineKeyLast4 ?? null;
  // SOC-33 (revised): the permission the API enforces, not the neighbouring settings one
  const mayEdit = can('account:connect');
  const save = useMutation({
    mutationFn: (apiKey: string | null) =>
      unwrap(api.post<{ stored: boolean }>('/portal/settings/postroutine', { apiKey })),
    onSuccess: async (_r, apiKey) => {
      setDraft('');
      setReplacing(false);
      await refresh();
      await qc.invalidateQueries({ queryKey: ['accounts'] });
      await qc.invalidateQueries({ queryKey: ['account-onboarding'] });
      toast(apiKey === null ? 'API key removed' : 'API key saved');
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  // UI-73: nothing to ask for where the deployment supplies the credential itself
  if (view?.keySource === 'server')
    return (
      <Step n={1} title={`${label} API key`} state="done">
        <p className="muted">
          This deployment supplies its own {label} key for every workspace, so there is nothing to
          add here.
        </p>
      </Step>
    );
  const linkedCount = (view?.accounts ?? []).filter((a) => a.linked).length;
  const removeWarning =
    linkedCount === 0
      ? 'No connected accounts depend on this key.'
      : `Removing the key will disconnect ${linkedCount} account${linkedCount === 1 ? '' : 's'}.`;
  const form = (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(draft.trim());
      }}
    >
      <Field label="API key">
        <input
          className="input"
          type="password"
          autoComplete="off"
          placeholder="pr_live_…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={!mayEdit}
        />
      </Field>
      <button
        type="submit"
        className="btn btn-primary"
        disabled={!mayEdit || draft.trim() === '' || save.isPending}
      >
        Save key
      </button>
    </form>
  );
  return (
    <Step n={1} title={`${label} API key`} state={last4 ? 'done' : 'todo'}>
      <ErrorAlert error={save.error as ApiError | null} />
      {last4 ? (
        <>
          <div className="row">
            <span className="mono">•••• {last4}</span>
            <span className="muted">Stored encrypted. It is never shown again.</span>
            {mayEdit ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={save.isPending}
                  onClick={() => setReplacing((r) => !r)}
                >
                  Replace
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  aria-describedby="pr-key-remove-note"
                  disabled={save.isPending}
                  onClick={() => save.mutate(null)}
                >
                  Remove
                </button>
              </>
            ) : null}
          </div>
          <p className="muted" id="pr-key-remove-note">
            {removeWarning}
          </p>
          {replacing ? form : null}
        </>
      ) : (
        <>
          <p className="muted">
            From your {label} workspace, under API keys. It is stored encrypted, used only for this
            workspace, and never shown again.
          </p>
          {form}
        </>
      )}
    </Step>
  );
}

/**
 * SOC-35: the tester step. AutoDM cannot test this directly — it can only report whether the
 * gateway admits to holding an account, which is exactly what being a tester buys.
 */
function TesterStep({ view }: { view: OnboardingView }) {
  const label = gatewayLabel(view.gateway);
  const held = view.accounts.length;
  const state = view.stage === 'no_key' ? 'todo' : held === 0 ? 'blocked' : 'done';
  return (
    <Step
      n={2}
      title={`Tester access, and connected in ${gatewayLabel(view.gateway)}`}
      state={state}
    >
      {state === 'done' ? (
        <p className="muted">
          {label} holds {held} {held === 1 ? 'account' : 'accounts'} for this key, so the tester
          invitation has been accepted and the account is connected there.
        </p>
      ) : (
        <>
          <p className="muted">
            {label}&rsquo;s Instagram app is still in Meta&rsquo;s review, so it can only serve
            accounts added to it as testers. Until that is done the account cannot be connected and
            will not appear below.
          </p>
          <ol className="conn-howto">
            <li>
              Ask {label} to add the Instagram account as a tester of their Meta app, giving them
              the exact username.
            </li>
            <li>
              Accept the invitation while logged in as that account, at{' '}
              <span className="mono">instagram.com/accounts/manage_access/</span> &rarr; Tester
              invites.
            </li>
            <li>Connect the account inside {label}, then re-check here.</li>
          </ol>
          <p className="muted">
            The account must also be a Business or Creator account; a personal one cannot be
            connected at all.
          </p>
        </>
      )}
    </Step>
  );
}

/** SOC-29: the accounts the gateway holds, and the one next thing to do with each. */
function HeldAccounts({ view }: { view: OnboardingView }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const link = useMutation({
    mutationFn: (externalAccountId: string) =>
      unwrap(api.post<Account>('/portal/social-accounts/link', { externalAccountId })),
    onSuccess: async (a) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['accounts'] }),
        qc.invalidateQueries({ queryKey: ['account-onboarding'] }),
      ]);
      toast(`@${a.username} linked`);
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const linked = view.accounts.filter((a) => a.linked).length;
  const state =
    view.accounts.length === 0 ? 'todo' : linked === view.accounts.length ? 'done' : 'todo';
  return (
    <Step n={3} title="Linked to AutoDM" state={state}>
      <ErrorAlert error={link.error as ApiError | null} />
      {view.accounts.length === 0 ? (
        <p className="muted">Nothing to link yet: step 2 has to finish first.</p>
      ) : (
        <DataTable>
          <table className="table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Account ID</th>
                <th>Token</th>
                <th>Missing permissions</th>
                <th>Next</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {view.accounts.map((a) => (
                <tr key={a.externalAccountId}>
                  <td>@{a.username}</td>
                  <td className="mono">{a.externalAccountId}</td>
                  <td>
                    <Badge value={a.tokenValid ? 'valid' : 'invalid'} />
                  </td>
                  <td>
                    {a.missingScopes.length === 0
                      ? '—'
                      : a.missingScopes.map((p) => p.replace('instagram_business_', '')).join(', ')}
                  </td>
                  <td>
                    <Badge value={a.nextAction} />
                  </td>
                  <td className="actions">
                    {a.nextAction === 'link' && can('account:connect') ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={link.isPending}
                        onClick={() => link.mutate(a.externalAccountId)}
                      >
                        Link
                      </button>
                    ) : a.nextAction === 'ready' && a.accountId ? (
                      <Link className="btn btn-sm" to={`/app/accounts/${a.accountId}`}>
                        View
                      </Link>
                    ) : (
                      <span className="muted">Fix in {gatewayLabel(view.gateway)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      )}
    </Step>
  );
}

/**
 * SOC-35: one section that says where this workspace stands with the gateway and re-checks it on
 * demand. Absent entirely on a gateway that owns its own OAuth flow, where none of this applies.
 */
/** One query for the page and the card, so they agree and the gateway is asked once. */
function useOnboardingView() {
  return useQuery({
    queryKey: ['account-onboarding'],
    // A gateway without this capability answers `supported: false`; a 404 is not worth retrying.
    retry: false,
    queryFn: () => unwrap(api.get<OnboardingView>('/portal/social-accounts/onboarding')),
  });
}

function ConnectionCard() {
  const qc = useQueryClient();
  const view = useOnboardingView();
  // UI-72 (revised): the gateway takes seconds to answer, so say so rather than show nothing
  if (view.isPending)
    return (
      <Card title="Instagram connection">
        <p className="muted" data-testid="onboarding-checking">
          Checking where this workspace stands with the gateway…
        </p>
      </Card>
    );
  // UI-73: a refusal is the likeliest answer to a mistyped key; show it with the key step
  if (view.isError)
    return (
      <Card title="Instagram connection">
        <div data-testid="onboarding-card">
          <ErrorAlert error={view.error} />
          <ol className="conn-steps">
            <KeyStep gateway="postroutine" />
          </ol>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => qc.invalidateQueries({ queryKey: ['account-onboarding'] })}
          >
            Re-check
          </button>
        </div>
      </Card>
    );
  if (!view.data?.supported) return null;
  const v = view.data;
  // UI-73: a stage this bundle does not know degrades to the first step, never a crash
  const stage = STAGE[v.stage] ?? STAGE.no_key;
  return (
    <Card title={`Instagram connection via ${gatewayLabel(v.gateway)}`}>
      <div data-testid="onboarding-card">
        <div className={`alert alert-${stage.tone}`} role="status" data-testid="conn-stage">
          <strong>{stage.title}</strong> {stage.body}
        </div>
        <ol className="conn-steps">
          <KeyStep view={v} gateway={v.gateway} />
          <TesterStep view={v} />
          <HeldAccounts view={v} />
        </ol>
        <div className="row">
          <button
            type="button"
            className="btn btn-sm"
            disabled={view.isFetching}
            onClick={() => qc.invalidateQueries({ queryKey: ['account-onboarding'] })}
          >
            {view.isFetching ? 'Checking…' : 'Re-check'}
          </button>
          <span className="muted">
            A check asks {gatewayLabel(v.gateway)} directly; it takes a few seconds per account.
          </span>
        </div>
      </div>
    </Card>
  );
}

export function AccountsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const accounts = useAccounts();
  const onboarding = useOnboardingView();
  const [picker, setPicker] = useState(false);
  // UI-56: the detail page's Health card reads ['account', id]; a check refreshes it too
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['accounts'] }),
      qc.invalidateQueries({ queryKey: ['account'] }),
    ]);
  const connect = useMutation({
    mutationFn: async () => {
      const { url } = await unwrap(
        api.get<{ url: string }>('/portal/social-accounts/connect/instagram'),
      );
      // UI-70: the url is Instagram's dialog, or the portal's consent page with the fake gateway
      window.location.assign(url);
    },
    onSuccess: () => setPicker(false),
  });
  const useAction = (path: string, label: string) =>
    useMutation({
      mutationFn: (id: string) =>
        unwrap(api.post<Account>(`/portal/social-accounts/${id}/${path}`)),
      onSuccess: async () => {
        await invalidate();
        toast(label);
      },
      onError: (e: ApiError) => toast(e.message, 'error'),
    });
  // SOC-19 (revised 2): the check says when it renewed the token (it left its expiry window)
  const health = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.post<Account>(`/portal/social-accounts/${id}/health`)).then((after) => ({
        after,
        before: accounts.data?.items.find((x) => x.id === id)?.tokenStatus ?? null,
      })),
    onSuccess: async ({ after, before }) => {
      await invalidate();
      toast(
        before === 'expiring' && after.tokenStatus === 'valid'
          ? 'Connection tested; the token was renewed'
          : 'Connection tested',
      );
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const sync = useAction('sync', 'Media synced');
  const disconnect = useAction('disconnect', 'Account disconnected');
  return (
    <>
      <PageHeader
        title="Social Accounts"
        subtitle="Connect and manage the professional accounts your automations run on"
        actions={
          // UI-72: on a gateway that owns its own connect flow the picker would start an OAuth
          // round trip the adapter refuses; the connection section below is the way in.
          can('account:connect') &&
          !onboarding.isPending &&
          !onboarding.isError &&
          !onboarding.data?.supported ? (
            <button type="button" className="btn btn-primary" onClick={() => setPicker(true)}>
              Connect account
            </button>
          ) : null
        }
      />
      <ErrorAlert error={accounts.error} />
      <ErrorAlert error={connect.error as ApiError | null} />
      <ConnectionCard />
      <DataTable>
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Username</th>
              <th>Platform</th>
              <th>Type</th>
              <th>Account ID</th>
              <th>Status</th>
              <th>Connected on</th>
              <th>Token</th>
              <th>Active automations</th>
              <th>Last sync</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(accounts.data?.items ?? []).map((a) => (
              <tr key={a.id}>
                <td>
                  <Link to={`/app/accounts/${a.id}`}>{a.name}</Link>
                </td>
                <td>@{a.username}</td>
                <td>{a.platform}</td>
                <td>{a.accountType}</td>
                <td className="mono">{a.externalId}</td>
                <td>
                  <Badge value={a.status} />
                </td>
                <td>{fmtDate(a.connectedAt)}</td>
                <td>
                  <Badge value={a.tokenStatus} />
                </td>
                <td>{a.activeAutomations}</td>
                <td>{relTime(a.lastSyncedAt)}</td>
                <td className="actions">
                  <Link className="btn btn-sm" to={`/app/accounts/${a.id}`}>
                    View
                  </Link>{' '}
                  {can('account:connect') && a.status !== 'connected' ? (
                    // UI-50: a connected account has nothing to reconnect
                    <button type="button" className="btn btn-sm" onClick={() => connect.mutate()}>
                      Reconnect
                    </button>
                  ) : null}{' '}
                  <button type="button" className="btn btn-sm" onClick={() => sync.mutate(a.id)}>
                    Refresh
                  </button>{' '}
                  <button type="button" className="btn btn-sm" onClick={() => health.mutate(a.id)}>
                    Test connection
                  </button>{' '}
                  {can('account:manage') && a.status !== 'disconnected' ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => disconnect.mutate(a.id)}
                    >
                      Disconnect
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {accounts.data && accounts.data.items.length === 0 ? (
              <tr>
                <td colSpan={11}>
                  <Empty>
                    No accounts connected yet. Connect your Instagram professional account to get
                    started.
                  </Empty>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </DataTable>
      {picker ? (
        <Modal title="Select a platform" onClose={() => setPicker(false)}>
          <p className="muted">
            Instagram professional accounts are supported today. Other platforms are on the roadmap.
          </p>
          <div className="grid grid-3">
            {PLATFORMS.map((p) => (
              <button
                type="button"
                key={p}
                className="card"
                disabled={p !== 'Instagram' || connect.isPending}
                onClick={() => connect.mutate()}
                style={{
                  cursor: p === 'Instagram' ? 'pointer' : 'not-allowed',
                  opacity: p === 'Instagram' ? 1 : 0.5,
                  textAlign: 'left',
                }}
              >
                <strong>{p}</strong>
                <div className="muted small">
                  {p === 'Instagram'
                    ? 'Comments, messages and mentions via the Instagram API'
                    : 'Coming soon'}
                </div>
              </button>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

export function AccountDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const [kind, setKind] = useState<'post' | 'reel' | 'story'>('post');
  // UI-69: re-authorizing runs the connect flow again; the callback updates this row (SOC-08)
  const reauthorize = useMutation({
    mutationFn: async () => {
      const { url } = await unwrap(
        api.get<{ url: string }>('/portal/social-accounts/connect/instagram'),
      );
      window.location.assign(url);
    },
  });
  const a = useQuery({
    queryKey: ['account', id],
    queryFn: () => unwrap(api.get<Account>(`/portal/social-accounts/${id}`)),
  });
  const media = useQuery({
    queryKey: ['media', id, kind],
    queryFn: () =>
      unwrap(api.get<Items<Media>>(`/portal/social-accounts/${id}/media${qs({ kind })}`)),
  });
  const acc = a.data;
  return (
    <>
      <PageHeader
        title={acc ? `@${acc.username}` : 'Account'}
        subtitle={acc?.name}
        actions={
          <>
            {acc && acc.status === 'connected' && can('account:connect') ? (
              <button
                type="button"
                className="btn"
                disabled={reauthorize.isPending}
                onClick={() => reauthorize.mutate()}
              >
                Re-authorize permissions
              </button>
            ) : null}
            <Link className="btn" to="/app/accounts">
              Back
            </Link>
          </>
        }
      />
      <ErrorAlert error={a.error} />
      <ErrorAlert error={reauthorize.error as ApiError | null} />
      {acc ? (
        <div className="grid grid-3">
          <Card title="Account information">
            <dl className="kv">
              <dt>Account name</dt>
              <dd>{acc.name}</dd>
              <dt>Username</dt>
              <dd>@{acc.username}</dd>
              <dt>Account ID</dt>
              <dd className="mono">{acc.externalId}</dd>
              <dt>Account type</dt>
              <dd>{acc.accountType}</dd>
              <dt>Platform</dt>
              <dd>{acc.platform}</dd>
              <dt>Connected</dt>
              <dd>{fmtDate(acc.connectedAt)}</dd>
              <dt>Last sync</dt>
              <dd>{fmtDate(acc.lastSyncedAt)}</dd>
              <dt>Profile image</dt>
              <dd>
                {acc.profilePictureUrl ? (
                  <img
                    src={acc.profilePictureUrl}
                    alt=""
                    width={48}
                    height={48}
                    style={{ borderRadius: 24 }}
                  />
                ) : (
                  'none'
                )}
              </dd>
              <dt>Webhooks</dt>
              <dd>
                {Object.values(acc.webhooks).every((v) => v === 'active')
                  ? 'Healthy'
                  : 'Attention needed'}
              </dd>
            </dl>
          </Card>
          <Card title="Permissions">
            <dl className="kv">
              {PERMISSION_LABELS.map(([scope, label]) => (
                <Fragment key={scope}>
                  <dt>{label}</dt>
                  <dd>{acc.permissions.includes(scope) ? '✓ granted' : '— not granted'}</dd>
                </Fragment>
              ))}
            </dl>
            <div className="card-title" style={{ marginTop: 14 }}>
              Webhooks
            </div>
            <dl className="kv">
              {Object.entries(acc.webhooks).map(([k, v]) => (
                <>
                  <dt key={`${k}-k`}>{k}</dt>
                  <dd key={`${k}-v`}>
                    <Badge value={v} />
                  </dd>
                </>
              ))}
            </dl>
          </Card>
          <Card title="Health">
            <dl className="kv">
              <dt>API connection</dt>
              <dd>
                <Badge value={acc.health} />
              </dd>
              <dt>Token</dt>
              <dd>
                <Badge value={acc.tokenStatus} />
              </dd>
              <dt>Status</dt>
              <dd>
                <Badge value={acc.status} />
              </dd>
              <dt>Last event</dt>
              <dd>{relTime(acc.lastEventAt)}</dd>
              <dt>Last check</dt>
              <dd>{relTime(acc.lastHealthCheckAt)}</dd>
            </dl>
          </Card>
        </div>
      ) : null}
      <Card title="Media" className="card">
        <div className="tabs">
          {(['post', 'reel', 'story'] as const).map((k) => (
            <button
              type="button"
              key={k}
              className={kind === k ? 'active' : ''}
              onClick={() => setKind(k)}
            >
              {plural(k)}
            </button>
          ))}
        </div>
        <DataTable>
          <table className="table">
            <thead>
              <tr>
                <th>External ID</th>
                <th>Caption</th>
                <th>Type</th>
                <th>Published</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {(media.data?.items ?? []).map((m) => (
                <tr key={m.id}>
                  <td className="mono">{m.externalId}</td>
                  <td className="truncate">{m.caption ?? '—'}</td>
                  <td>{m.mediaType ?? '—'}</td>
                  <td>{fmtDate(m.publishedAt)}</td>
                  <td>
                    {m.permalink ? (
                      <a href={m.permalink} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
              {media.data?.items.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <Empty>No {plural(kind)} synced. Use Refresh on the accounts page.</Empty>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </DataTable>
      </Card>
    </>
  );
}
